import { useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { BarChart3, RefreshCw, Users, Building2, Megaphone, Linkedin, Receipt, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { tracker } from "../../analytics";
import AdminPanelSidebar, {
  SIDEBAR_OPEN_WIDTH,
  SIDEBAR_CLOSED_WIDTH,
} from "./components/AdminPanelSidebar";
import AnalyticsScreen from "../AnalyticsScreen/AnalyticsScreen";
import DataUpdateScreen from "../DataUpdateScreen/DataUpdateScreen";
import UserManagement from "../SettingsScreen/components/UserManagement/UserManagement";
import OrganizationsRouter from "./components/OrganizationDetail/OrganizationsRouter";
import MarketingScreen from "./components/Marketing/MarketingScreen";
import LinkedInOutreachScreen from "./components/LinkedInOutreach/LinkedInOutreachScreen";
import FacturationScreen from "./components/Facturation/FacturationScreen";
import InboxScreen from "./components/Inbox/InboxScreen";

const ADMIN_TABS = [
  { key: "inbox", path: "inbox", icon: <Mail size={20} /> },
  { key: "analytics", path: "analytics", icon: <BarChart3 size={20} /> },
  { key: "marketing", path: "marketing", icon: <Megaphone size={20} /> },
  { key: "linkedin-outreach", path: "linkedin-outreach", icon: <Linkedin size={20} /> },
  { key: "facturation", path: "facturation", icon: <Receipt size={20} /> },
  { key: "data-update", path: "data-update", icon: <RefreshCw size={20} /> },
  { key: "users", path: "users", icon: <Users size={20} /> },
  { key: "organizations", path: "organizations", icon: <Building2 size={20} /> },
];

const AdminPanelScreen = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  // Derive active tab from URL
  const pathAfterAdmin = location.pathname.replace(/^\/admin\/?/, "").split("/")[0];
  const activeKey = ADMIN_TABS.find((t) => t.key === pathAfterAdmin)?.key || "analytics";

  const tabs = ADMIN_TABS.map((tab) => ({
    ...tab,
    label:
      tab.key === "inbox"
        ? "Inbox"
        : tab.key === "analytics"
        ? t("analytics")
        : tab.key === "data-update"
          ? t("du_title")
          : tab.key === "users"
            ? t("user_management")
            : tab.key === "organizations"
              ? t("platform_management", "Organizations")
              : tab.key === "linkedin-outreach"
                ? "LinkedIn Outreach"
                : tab.key === "facturation"
                  ? "Facturation"
                  : t("admin_marketing"),
  }));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleTabChange = (tabKey) => {
    navigate(`/admin/${tabKey}`);
    if (isMobile) {
      setMobileOpen(false);
    }

    ReactGA.event({
      category: "AdminPanel",
      action: "Tab Change",
      label: `Tab: ${tabKey}`,
    });
    tracker.event({
      category: "AdminPanel",
      action: "Tab Change",
      label: `Tab: ${tabKey}`,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        backgroundColor: theme.palette.background.default,
        height: "100vh",
        direction: currentLang === "ar" ? "rtl" : "ltr",
        overflow: "hidden",
      }}
    >
      <AdminPanelSidebar
        tabs={tabs}
        activeTab={activeKey}
        onTabChange={handleTabChange}
        mobileOpen={mobileOpen}
        onDrawerToggle={handleDrawerToggle}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 2 },
          bgcolor: theme.palette.background.default,
          height: isMobile ? `calc(100vh - 64px)` : "100vh",
          overflow: "hidden",
          width: isMobile
            ? "100%"
            : `calc(100% - ${sidebarCollapsed ? SIDEBAR_CLOSED_WIDTH : SIDEBAR_OPEN_WIDTH}px)`,
          transition: "width 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Routes>
            <Route index element={<Navigate to="analytics" replace />} />
            <Route path="analytics/*" element={<AnalyticsScreen />} />
            <Route path="data-update/*" element={<DataUpdateScreen />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="organizations/*" element={<OrganizationsRouter />} />
            <Route path="marketing/*" element={<MarketingScreen />} />
            <Route path="linkedin-outreach/*" element={<LinkedInOutreachScreen />} />
            <Route path="inbox/*" element={<InboxScreen />} />
            <Route path="facturation/*" element={<FacturationScreen />} />
            <Route path="*" element={<Navigate to="analytics" replace />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPanelScreen;
